// src/components/Playlists.js
import { useState, useEffect } from 'react';
import SpotifyWebApi from 'spotify-web-api-js';
import Navbar from './Navbar';

const spotifyApi = new SpotifyWebApi();

const Playlists = ({ token }) => {
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('playlists');
  const [shuffleEnabled, setShuffleEnabled] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);

  // Fetch playlists on component mount
  useEffect(() => {
    const fetchPlaylists = async () => {
      if (!token) return;
      try {
        setLoading(true);
        spotifyApi.setAccessToken(token);
        const response = await spotifyApi.getUserPlaylists();
        setPlaylists(response.items);
      } catch (err) {
        setError('Failed to fetch playlists');
        console.error('Error fetching playlists:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlaylists();
  }, [token]);

  // Fetch tracks when a playlist is selected
  useEffect(() => {
    const fetchTracks = async () => {
      if (!selectedPlaylist) return;
      try {
        setLoading(true);
        const response = await spotifyApi.getPlaylistTracks(selectedPlaylist.id);
        setTracks(response.items);
      } catch (err) {
        setError('Failed to fetch tracks');
        console.error('Error fetching tracks:', err);
      } finally {
        setLoading(false);
      }
    };

    if (selectedPlaylist) {
      fetchTracks();
    }
  }, [selectedPlaylist]);

  // Keep track of currently playing song
  useEffect(() => {
    const fetchCurrentlyPlaying = async () => {
      if (!token) return;
      try {
        const response = await spotifyApi.getMyCurrentPlaybackState();
        setCurrentlyPlaying(response);
      } catch (err) {
        console.error('Error fetching current playback:', err);
      }
    };

    fetchCurrentlyPlaying();
    const interval = setInterval(fetchCurrentlyPlaying, 1000);
    return () => clearInterval(interval);
  }, [token]);

  const handlePlayPlaylist = async (playlistUri) => {
    try {
      // First set the shuffle state
      await spotifyApi.setShuffle(shuffleEnabled);
      
      // Then start playing the playlist
      await spotifyApi.play({
        context_uri: playlistUri
      });
    } catch (err) {
      setError('Failed to play playlist. Make sure you have a Premium account and an active device.');
      console.error('Error playing playlist:', err);
    }
  };

  const handlePlayTrack = async (trackUri, index) => {
    try {
      // First set the shuffle state
      await spotifyApi.setShuffle(shuffleEnabled);
      
      // Then start playing from the selected track
      await spotifyApi.play({
        context_uri: selectedPlaylist.uri,
        offset: { position: index }
      });
    } catch (err) {
      setError('Failed to play track. Make sure you have a Premium account and an active device.');
      console.error('Error playing track:', err);
    }
  };

  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <Navbar />
        <div className="row">
          <div className="col-md-9 p-4">
            <div className="d-flex align-items-center">
              <h2 className="me-3">Loading...</h2>
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="container-fluid">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}

        <div className="row">
          <div className="col-md-9 p-4">
            {viewMode === 'playlists' ? (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2 className="mb-0">Your Playlists</h2>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="shuffleToggle"
                      checked={shuffleEnabled}
                      onChange={(e) => setShuffleEnabled(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="shuffleToggle">
                      <i className="bi bi-shuffle me-2"></i>
                      Shuffle Play
                    </label>
                  </div>
                </div>

                <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-4">
                  {playlists.map(playlist => (
                    <div key={playlist.id} className="col">
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="position-relative" style={{ width: '100%', paddingBottom: '100%' }}>
                          {playlist.images?.[0] ? (
                            <img
                              src={playlist.images[0].url}
                              alt={playlist.name}
                              className="position-absolute top-0 start-0 w-100 h-100"
                              style={{ objectFit: 'cover' }}
                            />
                          ) : (
                            <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark d-flex align-items-center justify-content-center">
                              <i className="bi bi-music-note text-light" style={{ fontSize: '2rem' }}></i>
                            </div>
                          )}
                          {currentlyPlaying?.context?.uri === playlist.uri && (
                            <div className="position-absolute top-0 end-0 m-2 badge bg-success">
                              <i className="bi bi-volume-up me-1"></i>
                              Playing
                            </div>
                          )}
                        </div>
                        <div className="card-body">
                          <h5 className="card-title text-truncate mb-1">{playlist.name}</h5>
                          <p className="card-text">
                            <small className="text-muted">
                              By {playlist.owner.display_name}<br />
                              {playlist.tracks.total} tracks
                            </small>
                          </p>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-success flex-grow-1"
                              onClick={() => handlePlayPlaylist(playlist.uri)}
                            >
                              <i className="bi bi-play-fill"></i> Play
                            </button>
                            <button
                              className="btn btn-outline-secondary flex-grow-1"
                              onClick={() => {
                                setSelectedPlaylist(playlist);
                                setViewMode('tracks');
                              }}
                            >
                              View Tracks
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h2 className="mb-0">{selectedPlaylist?.name}</h2>
                    <p className="text-muted mb-0">
                      By {selectedPlaylist?.owner.display_name} • {tracks.length} tracks
                    </p>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="shuffleToggle"
                        checked={shuffleEnabled}
                        onChange={(e) => setShuffleEnabled(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="shuffleToggle">
                        <i className="bi bi-shuffle me-2"></i>
                        Shuffle
                      </label>
                    </div>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setViewMode('playlists');
                        setSelectedPlaylist(null);
                      }}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Back to Playlists
                    </button>
                  </div>
                </div>

                {selectedPlaylist?.images?.[0] && (
                  <div className="mb-4">
                    <img
                      src={selectedPlaylist.images[0].url}
                      alt={selectedPlaylist.name}
                      style={{ width: '200px', height: '200px', objectFit: 'cover' }}
                      className="rounded shadow-sm"
                    />
                  </div>
                )}

                <div className="list-group">
                  {tracks.map((item, index) => (
                    <button
                      key={item.track.id}
                      className={`list-group-item list-group-item-action d-flex align-items-center gap-3 ${
                        currentlyPlaying?.item?.id === item.track.id ? 'active' : ''
                      }`}
                      onClick={() => handlePlayTrack(item.track.uri, index)}
                    >
                      <div className="d-flex align-items-center" style={{ width: '40px' }}>
                        {currentlyPlaying?.item?.id === item.track.id ? (
                          <i className="bi bi-volume-up text-success"></i>
                        ) : (
                          <span className="text-muted">{index + 1}</span>
                        )}
                      </div>
                      
                      {item.track.album.images?.[2] && (
                        <img
                          src={item.track.album.images[2].url}
                          alt={item.track.album.name}
                          style={{ width: '40px', height: '40px' }}
                          className="rounded"
                        />
                      )}
                      
                      <div className="d-flex flex-column flex-grow-1">
                        <strong className={currentlyPlaying?.item?.id === item.track.id ? 'text-white' : ''}>
                          {item.track.name}
                        </strong>
                        <small className={currentlyPlaying?.item?.id === item.track.id ? 'text-white-50' : 'text-muted'}>
                          {item.track.artists.map(artist => artist.name).join(', ')}
                        </small>
                      </div>
                      
                      <div className={`d-none d-md-block ${
                        currentlyPlaying?.item?.id === item.track.id ? 'text-white-50' : 'text-muted'
                      }`}>
                        {item.track.album.name}
                      </div>
                      
                      <div className={currentlyPlaying?.item?.id === item.track.id ? 'text-white-50' : 'text-muted'}
                           style={{ minWidth: '60px', textAlign: 'right' }}>
                        {formatTime(item.track.duration_ms)}
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Playlists;